/**
 * cart-discount.js
 * Handles coupon/discount code apply and remove via AJAX.
 * Works with the unified single-section cart template where
 * main-cart-items and main-cart-footer are inside the same section.
 */

class CartDiscount extends HTMLElement {
  constructor() {
    super();
    this.form = this.querySelector('.f-cart-discount__form');
    this.input = this.querySelector('input[name="discount"]');
    this.submitBtn = this.querySelector('[type="submit"]');
    this.errorMessage = this.querySelector('.form-message--error');
  }

  connectedCallback() {
    if (this.form) {
      this.form.addEventListener('submit', this.onSubmit.bind(this));
    }
  }

  /**
   * Resolves the single section ID dynamically from the DOM.
   * Both main-cart-items and main-cart-footer share the same section.id
   * in the unified template — read it from whichever element is available.
   */
  getSectionId() {
    return (
      document.getElementById('main-cart-items')?.dataset.id ||
      document.getElementById('main-cart-footer')?.dataset.id ||
      null
    );
  }

  /**
   * Returns sections array for /cart/update.js or /cart/change.js calls.
   * Unified template: one section ID renders both items + footer.
   */
  getSectionsToRender() {
    const sectionId = this.getSectionId();
    if (!sectionId) return [];
    return [sectionId];
  }

  /**
   * After getting parsedState back, re-render all relevant DOM regions
   * using the section HTML returned by Shopify.
   */
  renderSections(parsedState) {
    const sectionId = this.getSectionId();
    if (!sectionId || !parsedState.sections?.[sectionId]) return;

    const sectionHTML = parsedState.sections[sectionId];

    // Re-render cart items table
    const itemsTarget = document.querySelector('#main-cart-items .js-contents');
    if (itemsTarget) {
      itemsTarget.innerHTML = this.extractHTML(sectionHTML, '#main-cart-items .js-contents');
    }

    // Re-render cart sidebar/footer (subtotal, discount tags, buttons)
    const footerTarget = document.querySelector('#main-cart-footer .js-contents');
    if (footerTarget) {
      footerTarget.innerHTML = this.extractHTML(sectionHTML, '#main-cart-footer .js-contents');
    }
  }

  extractHTML(html, selector) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.querySelector(selector)?.innerHTML ?? '';
  }

  setLoading(isLoading) {
    if (!this.submitBtn) return;
    if (isLoading) {
      this.submitBtn.setAttribute('disabled', 'disabled');
      this.submitBtn.classList.add('loading');
    } else {
      this.submitBtn.removeAttribute('disabled');
      this.submitBtn.classList.remove('loading');
    }
  }

  showError(message) {
    if (this.errorMessage) {
      this.errorMessage.textContent = message;
      this.errorMessage.style.display = 'block';
    }
  }

  clearError() {
    if (this.errorMessage) {
      this.errorMessage.textContent = '';
      this.errorMessage.style.display = 'none';
    }
  }

  async onSubmit(event) {
    event.preventDefault();
    this.clearError();

    const discountCode = this.input?.value?.trim();
    if (!discountCode) {
      this.showError(
        window.FoxThemeStrings?.discountError || 'Please enter a discount code.'
      );
      return;
    }

    this.setLoading(true);

    try {
      const sections = this.getSectionsToRender();

      // Step 1: Set the discount cookie via Shopify storefront
      await fetch(
        `/cart?discount=${encodeURIComponent(discountCode)}`,
        { method: 'GET' }
      );

      // Step 2: Call /cart/update.js with section rendering
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          updates: {},
          sections,
          sections_url: window.location.pathname,
        }),
      });

      if (!response.ok) throw new Error('Cart update failed');
      const parsedState = await response.json();

      // Step 3: Validate discount was actually applied
      const appliedCodes = [
        ...(parsedState.cart_level_discount_applications || []).map(d => d.title?.toUpperCase()),
        ...(parsedState.items || []).flatMap(item =>
          (item.line_level_discount_allocations || []).map(a =>
            a.discount_application?.title?.toUpperCase()
          )
        ),
      ].filter(Boolean);

      if (!appliedCodes.includes(discountCode.toUpperCase())) {
        this.showError(
          window.FoxThemeStrings?.discountInvalid ||
          'This discount code is not valid or has expired.'
        );
        this.setLoading(false);
        return;
      }

      // Step 4: Re-render both cart items + footer from section HTML
      this.renderSections(parsedState);

      // Step 5: Clear input
      if (this.input) this.input.value = '';

      // Step 6: Emit events for minicart / third-party apps
      this.emitCartUpdate(parsedState);

    } catch (err) {
      console.error('Discount apply error:', err);
      this.showError(
        window.FoxThemeStrings?.cartError || 'Something went wrong. Please try again.'
      );
    } finally {
      this.setLoading(false);
    }
  }

  emitCartUpdate(parsedState) {
    if (window.FoxThemeEvents && window.PUB_SUB_EVENTS?.cartUpdate) {
      window.FoxThemeEvents.emit(PUB_SUB_EVENTS.cartUpdate, parsedState);
    }
    document.dispatchEvent(
      new CustomEvent('cart:updated', { detail: { cart: parsedState } })
    );
  }
}

customElements.define('cart-discount', CartDiscount);


/**
 * CartDiscountRemoveHandler
 *
 * Uses document-level event delegation instead of extending HTMLButtonElement.
 *
 * WHY: Safari does not support customized built-in elements
 * (`customElements.define(..., { extends: 'button' })`).
 * Without this fix, connectedCallback never fires in Safari, the click
 * handler is never attached, and the button gets permanently stuck in
 * its loading/spinning state after one click.
 *
 * Event delegation also works automatically for remove buttons that are
 * injected into the DOM after section re-renders.
 */
const CartDiscountRemoveHandler = {
  _bound: false,

  init() {
    if (this._bound) return;
    this._bound = true;
    document.addEventListener('click', (e) => {
      const btn = e.target.closest(
        'button[is="cart-discount-remove"], .cart-discounts__remove'
      );
      if (btn) {
        e.preventDefault();
        this.onRemove(btn);
      }
    });
  },

  getSectionId() {
    return (
      document.getElementById('main-cart-items')?.dataset.id ||
      document.getElementById('main-cart-footer')?.dataset.id ||
      null
    );
  },

  getSectionsToRender() {
    const sectionId = this.getSectionId();
    return sectionId ? [sectionId] : [];
  },

  extractHTML(html, selector) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.querySelector(selector)?.innerHTML ?? '';
  },

  renderSections(parsedState) {
    const sectionId = this.getSectionId();
    if (!sectionId || !parsedState.sections?.[sectionId]) return;

    const sectionHTML = parsedState.sections[sectionId];

    const itemsTarget = document.querySelector('#main-cart-items .js-contents');
    if (itemsTarget) {
      itemsTarget.innerHTML = this.extractHTML(sectionHTML, '#main-cart-items .js-contents');
    }

    const footerTarget = document.querySelector('#main-cart-footer .js-contents');
    if (footerTarget) {
      footerTarget.innerHTML = this.extractHTML(sectionHTML, '#main-cart-footer .js-contents');
    }
  },

  setLoading(btn, isLoading) {
    if (isLoading) {
      btn.setAttribute('disabled', 'disabled');
      btn.classList.add('loading');
    } else {
      btn.removeAttribute('disabled');
      btn.classList.remove('loading');
    }
  },

  async onRemove(btn) {
    this.setLoading(btn, true);

    try {
      const sections = this.getSectionsToRender();

      // Step 1: Clear the discount cookie by passing empty string
      await fetch('/cart?discount=', { method: 'GET' });

      // Step 2: Re-render sections via cart/update.js
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          updates: {},
          sections,
          sections_url: window.location.pathname,
        }),
      });

      if (!response.ok) throw new Error('Cart update failed');
      const parsedState = await response.json();

      // Step 3: Re-render — btn will be removed from DOM as part of re-render
      this.renderSections(parsedState);

      // Step 4: Emit events
      if (window.FoxThemeEvents && window.PUB_SUB_EVENTS?.cartUpdate) {
        window.FoxThemeEvents.emit(PUB_SUB_EVENTS.cartUpdate, parsedState);
      }
      document.dispatchEvent(
        new CustomEvent('cart:updated', { detail: { cart: parsedState } })
      );

    } catch (err) {
      console.error('Discount remove error:', err);
      // Only reset button state if it's still in the DOM (re-render didn't happen)
      if (document.contains(btn)) {
        this.setLoading(btn, false);
      }
    }
  },
};

// Initialise as early as possible
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => CartDiscountRemoveHandler.init());
} else {
  CartDiscountRemoveHandler.init();
}
